import { useCallback, useState } from "react";

// Strike-zone box geometry. Height is locked to width * 8/9 (the legacy
// ClickZone aspect), so the box is resized in one degree of freedom: width.
export const ZONE_ASPECT = 8 / 9;
// The box geometry is stored as FRACTIONS of the responsive .video-box
// (x/size are fractions of its width, y of its height) so the box scales with
// the video. These bound the size fraction (never 0 -> divide-by-zero, never
// larger than the frame).
export const MIN_SIZE = 0.06;
export const MAX_SIZE = 0.9;

// rendered-pixel within the box -> strike-zone unit (0..90). `sizePx` is the
// box's ACTUAL rendered width in px, so the conversion is identical at any
// video size — pitch locations are preserved byte-for-byte (legacy processCord).
export const pxToUnit = (px, sizePx) => (px * 90) / sizePx;
// strike-zone unit -> rendered px within the box (legacy reverseCord).
export const unitToPx = (unit, sizePx) => (unit / 90) * sizePx;

// y-unit (0..90, computed against the box's WIDTH like x — see pxToUnit) ->
// CSS `top` PERCENTAGE within the box. The box's rendered height is locked to
// width * ZONE_ASPECT, so a plain (y/90)*100 used as `top` gets resolved
// against that shorter height and the marker renders too high. Scale by
// 1/ZONE_ASPECT to correct for the width->height mismatch before it's used as
// a `top` percentage. (x keeps using (x/90)*100 for `left` — no correction
// needed there since left resolves against the same width the unit is based on.)
export const unitYToTopPct = (y) => (y / 90) * 100 * (1 / ZONE_ASPECT);

// A click's viewport coords -> strike-zone units, given the box's rendered
// rect (left/top) and rendered width in px. Mirrors the legacy click math.
export const clickToUnit = (clientX, clientY, left, top, sizePx) => ({
  x: pxToUnit(clientX - left, sizePx),
  y: pxToUnit(clientY - top, sizePx),
});

export const clampSize = (size) => Math.min(MAX_SIZE, Math.max(MIN_SIZE, size));
export const applyMove = (pos, delta) => pos + delta;
export const applyResize = (size, delta) => clampSize(size + delta);

// A sensible starting box (fractions of the video-box). The recorder drags/
// resizes it onto the strike zone each camera move; it now scales with the video.
const defaultBox = () => ({
  x: 0.38,
  y: 0.16,
  size: 0.22,
});

/**
 * Owns the strike-zone overlay box (position + size) and the operations that
 * mutate it — keyboard nudges (WASD/E/R via useHotkeys) and, later, mouse
 * drag/resize (Task 3b). Coordinate conversion lives here too so the overlay
 * and the click-to-submit path share one source of truth.
 */
export const useStrikeZone = (initial) => {
  const [box, setBox] = useState(() => ({ ...defaultBox(), ...initial }));

  const move = useCallback(
    (dx, dy) =>
      setBox((b) => ({ ...b, x: applyMove(b.x, dx), y: applyMove(b.y, dy) })),
    []
  );
  const resize = useCallback(
    (delta) => setBox((b) => ({ ...b, size: applyResize(b.size, delta) })),
    []
  );
  // Absolute setters for mouse drag (Task 3b).
  const setPosition = useCallback((x, y) => setBox((b) => ({ ...b, x, y })), []);
  const setSize = useCallback(
    (size) => setBox((b) => ({ ...b, size: clampSize(size) })),
    []
  );

  return { box, move, resize, setPosition, setSize };
};
